import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Outlet, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './components/auth/AuthContext';
import Navbar from './components/Navbar';
import Home from './pages/Home';
import Courses from './pages/Courses';
import CourseDetail from './pages/CourseDetail';
import Contact from './pages/Contact';
import About from './pages/About';
import Dashboard from './pages/Dashboard';
import Learn from './pages/Learn';
import Certificates from './pages/Certificates';
import VerifyCertificate from './pages/VerifyCertificate';
import Account from './pages/Account';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import AdminDashboard from './pages/admin/AdminDashboard';
import CourseEditor from './pages/admin/CourseEditor';
import ProtectedRoute from './components/ProtectedRoute';
import EventDetail from './pages/EventDetail';
import ProgramDetail from './pages/ProgramDetail';
import SignUp from './pages/SignUp';
import Login from './pages/Login';
import Footer from './components/Footer';
import WhatsAppChat from './components/WhatsAppChat';
import ExportMentorshipProgram from './pages/emp/ExportMentorshipProgram';
import FoodstuffExportMasterclass from './pages/fem/FoodstuffExportMasterclass';
import WhatsAppClassLayout from './pages/whatsappclass/WhatsAppClassLayout';
import Products from './pages/Products';
import Zero2Export from './pages/zero2export/Zero2Export';
import Campus2Container from './pages/campus2container/Campus2Container';
import LmsShell from './components/lms/LmsShell';
import PublicPage from './components/marketing/PublicPage';
import MarketingCoursesPage from './pages/marketing/CoursesPage';
import MarketingCourseDetailPage from './pages/marketing/CourseDetailPage';
import { GenericPageSkeleton } from './components/lms/Skeletons';
import { currentPathWithSearch, getSurface, lmsHref, marketingHref } from './lib/surface';

const MarketingLayout = () => (
  <>
    <Navbar />
    <Outlet />
    <Footer />
    <WhatsAppChat />
  </>
);

const LearningLayout = () => (
  <LmsShell>
    <Outlet />
  </LmsShell>
);

const LmsRedirect = () => {
  useEffect(() => {
    window.location.replace(lmsHref(currentPathWithSearch()));
  }, []);
  return <div className="min-h-screen bg-slate-50" aria-label="Opening the learning workspace" />;
};

const MarketingRedirect = () => {
  useEffect(() => {
    window.location.replace(marketingHref(currentPathWithSearch()));
  }, []);
  return <div className="min-h-screen bg-paper" aria-label="Opening the LSEM website" />;
};

const LmsLanding = () => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <GenericPageSkeleton />;
  return <Navigate to={isAuthenticated ? '/dashboard' : '/login'} replace />;
};

function AppRoutes() {
  const location = useLocation();
  const isLmsPath =
    location.pathname === '/' ||
    location.pathname.startsWith('/dashboard') ||
    location.pathname.startsWith('/account') ||
    location.pathname.startsWith('/certificates') ||
    location.pathname.startsWith('/learn/') ||
    location.pathname.startsWith('/admin') ||
    location.pathname === '/login' ||
    location.pathname === '/signup' ||
    location.pathname === '/forgot-password' ||
    location.pathname === '/reset-password';

  // On the marketing host, never mount the LMS route tree. Legacy LMS URLs are
  // handed to the LMS subdomain instead of rendering dashboard chrome here.
  const marketingHost = getSurface() === 'marketing';

  useEffect(() => {
    document.title = marketingHost
      ? 'Lagos School of Export Management | Export Training & Certification Nigeria'
      : 'LSEM Learning | Student workspace';
    const description = document.querySelector('meta[name="description"]');
    if (description) {
      description.setAttribute(
        'content',
        marketingHost
          ? 'Professional export training programs in Lagos, Nigeria. Learn international trade, export documentation, and global market strategies with industry experts.'
          : 'Sign in to the LSEM learning workspace to study your programme, track lessons and assessments, and manage your certificate record.',
      );
    }
  }, [marketingHost]);

  if (marketingHost) {
    return (
      <Routes>
        <Route element={<MarketingLayout />}>
          <Route path="/" element={<Home />} />
          <Route path="/courses" element={<MarketingCoursesPage />} />
          <Route path="/course/:id" element={<MarketingCourseDetailPage />} />
          <Route path="/event/:id" element={<EventDetail />} />
          <Route path="/program/:id" element={<ProgramDetail />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/about" element={<About />} />
          <Route path="/emp" element={<ExportMentorshipProgram />} />
          <Route path="/fem" element={<FoodstuffExportMasterclass />} />
          <Route path="/whatsappclass" element={<WhatsAppClassLayout />} />
          <Route path="/short-courses" element={<Products />} />
          <Route path="/products" element={<Products />} />
          <Route path="/zero2export" element={<Zero2Export />} />
          <Route path="/campus2container" element={<Campus2Container />} />
          <Route path="/verify" element={<PublicPage><VerifyCertificate /></PublicPage>} />
          <Route path="/verify/:code" element={<PublicPage><VerifyCertificate /></PublicPage>} />
          {isLmsPath && <Route path="*" element={<LmsRedirect />} />}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<SignUp />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route element={<LearningLayout />}>
        <Route path="/" element={<LmsLanding />} />
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
        <Route path="/courses" element={<ProtectedRoute><Courses /></ProtectedRoute>} />
        <Route path="/course/:id" element={<ProtectedRoute><CourseDetail /></ProtectedRoute>} />
        <Route path="/learn/:id" element={<ProtectedRoute><Learn /></ProtectedRoute>} />
        <Route path="/certificates" element={<ProtectedRoute><Certificates /></ProtectedRoute>} />
        <Route path="/verify" element={<VerifyCertificate />} />
        <Route path="/verify/:code" element={<VerifyCertificate />} />
        <Route path="/admin" element={<ProtectedRoute requireAdmin><AdminDashboard /></ProtectedRoute>} />
        <Route path="/admin/courses/:id" element={<ProtectedRoute requireAdmin><CourseEditor /></ProtectedRoute>} />
        <Route path="*" element={<MarketingRedirect />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppRoutes />
      </Router>
    </AuthProvider>
  );
}

export default App;
